import { formatPlainDate } from '../../core/dates/plain-date';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PublicDayAvailability } from '../../core/models/public-day-availability';
import { ChevronLeftIcon, ChevronRightIcon } from './booking-icons';
import { buildWeekPage } from './week-grid';

interface BookingCalendarProps {
    /** Which page of weeks is shown, 0 being the one that opens on today's week. */
    page: number;
    pages: number;
    /** Every day the server returned, keyed by its `"YYYY-MM-DD"`. */
    days: ReadonlyMap<string, PublicDayAvailability>;
    /** The server's own today, which is the only "today" this page believes in. */
    today: string;
    selected: string | null;
    onPageChange: (page: number) => void;
    onSelect: (date: string) => void;
}

/** Monday to Sunday, taken from a week that starts on a Monday — 2024-01-01 was one. */
const WEEKDAY_SAMPLE: readonly string[] = [
    '2024-01-01',
    '2024-01-02',
    '2024-01-03',
    '2024-01-04',
    '2024-01-05',
    '2024-01-06',
    '2024-01-07'
];

/**
 * The day grid, in pages of whole weeks starting from today's week, so today is always on the top
 * row. A month grid opened on the last day of a month is a page of greyed-out days with the
 * bookable ones behind an arrow.
 *
 * **Both arrows are bounded by the availability the server sent**, not by `new Date()`: the last
 * page is the one holding the window's last day.
 */
export function BookingCalendar({ page, pages, days, today, selected, onPageChange, onSelect }: BookingCalendarProps) {
    const { t, language } = useTranslation();
    const cells = buildWeekPage(today, page, days);
    const first = cells[0].date;
    const last = cells[cells.length - 1].date;

    return (
        <div>
            <div className="cal-hdr">
                <h2 className="cal-mo" aria-live="polite">
                    {formatPlainDate(first, language, { day: 'numeric', month: 'long' })} –{' '}
                    {formatPlainDate(last, language, { day: 'numeric', month: 'long', year: 'numeric' })}
                </h2>
                <div className="cal-nav">
                    <button
                        type="button"
                        disabled={page <= 0}
                        aria-label={t('booking.s2.prev_weeks')}
                        onClick={() => onPageChange(page - 1)}
                    >
                        <ChevronLeftIcon />
                    </button>
                    <button
                        type="button"
                        disabled={page >= pages - 1}
                        aria-label={t('booking.s2.next_weeks')}
                        onClick={() => onPageChange(page + 1)}
                    >
                        <ChevronRightIcon />
                    </button>
                </div>
            </div>

            <div className="cal-grid">
                {WEEKDAY_SAMPLE.map((date) => (
                    <div key={date} className="cal-wd" aria-hidden="true">
                        {formatPlainDate(date, language, { weekday: 'short' })}
                    </div>
                ))}

                {cells.map((cell) => {
                    const classes = ['cal-day'];

                    if (!cell.selectable) classes.push('dis');
                    if (cell.hasSlots) classes.push('has-s');
                    if (cell.date === today) classes.push('today');
                    if (cell.date === selected) classes.push('sel');

                    return (
                        <button
                            key={cell.date}
                            type="button"
                            className={classes.join(' ')}
                            disabled={!cell.selectable}
                            aria-pressed={cell.date === selected}
                            // The number alone reads as "14" out of context; the full date is what
                            // tells a screen-reader user which day of which month they are on.
                            aria-label={formatPlainDate(cell.date, language, {
                                day: 'numeric',
                                month: 'long',
                                weekday: 'long'
                            })}
                            onClick={() => onSelect(cell.date)}
                        >
                            {cell.startsMonth && (
                                <span className="cal-m" aria-hidden="true">
                                    {formatPlainDate(cell.date, language, { month: 'short' })}
                                </span>
                            )}
                            {cell.dayOfMonth}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
